/*
  Gabarit Box : choix d'offre, configurateur, CTA, rattrapage après achat.

  Deux chemins partent du même bouton :
  — « Continuer avec cette offre » (abonnement) ouvre le tunnel sans rien
    ajouter au panier : la box seule, au prix plein, serait un panier faux ;
  — « Ajouter au panier » (achat unique) ajoute vraiment, puis propose
    l'abonnement (brief §7). Le panier prime sur la proposition : au moindre
    échec on rend la main au formulaire du thème, qui sait afficher ses
    erreurs.
*/
(() => {
  const euro = (cents) =>
    new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(cents / 100);

  // Mémoire de la proposition, quand le réglage la limite à une par visite.
  // Par défaut elle s'ouvre à chaque ajout : une limite invisible se
  // diagnostique mal, y compris en recette.
  const CLE_SESSION = 'ltb-rattrapage-vu';

  const dejaVu = () => {
    try { return sessionStorage.getItem(CLE_SESSION) === '1'; } catch (e) { return false; }
  };

  const marquerVu = () => {
    try { sessionStorage.setItem(CLE_SESSION, '1'); } catch (e) { /* navigation privée */ }
  };

  class OffreBox {
    constructor(racine) {
      this.racine = racine;
      this.bloc = racine.querySelector('[data-ltb-offre]');
      if (!this.bloc) return;

      this.config = this.bloc.querySelector('[data-ltb-config]');
      this.cartes = [...this.bloc.querySelectorAll('.ltb-offre__card')];
      this.formules = [...this.bloc.querySelectorAll('.ltb-config__formule')];
      this.desEl = this.bloc.querySelector('[data-config-des]');
      this.conditionsEl = this.bloc.querySelector('[data-config-conditions]');

      this.qty = racine.querySelector('[data-pdp-qty]');
      this.cta = racine.querySelector('[data-pdp-cta]');
      this.ctaText = racine.querySelector('[data-pdp-cta-text]');
      this.form = racine.querySelector('form[action*="/cart/add"]');

      const brut = this.bloc.querySelector('[data-ltb-offre-data]');
      this.donnees = brut ? JSON.parse(brut.textContent) : {};

      this.mode = 'unique';
      this.formule = 'mensuel';
      this.labelUnique = this.ctaText ? this.ctaText.textContent.trim() : '';
      // Réponse de /cart/add.js gardée le temps du rattrapage : elle porte
      // les sections du panier, de quoi ouvrir le tiroir si l'offre est
      // déclinée, exactement comme l'aurait fait `product-form`.
      this.reponsePanier = null;
      this.panier = null;
      this.laisserPasser = false;

      this.brancher();
      this.rendre();
    }

    brancher() {
      this.bloc.querySelectorAll('[data-offre-input]').forEach((input) => {
        input.addEventListener('change', () => {
          if (!input.checked) return;
          this.mode = input.value;
          this.rendre();
        });
      });

      this.bloc.querySelectorAll('[data-formule-input]').forEach((input) => {
        input.addEventListener('change', () => {
          if (!input.checked) return;
          this.formule = input.value;
          this.rendre();
        });
      });

      // Le JS commun recalcule le libellé du CTA à chaque changement de
      // quantité ou de variante : on repasse derrière lui.
      const rattraper = () => window.setTimeout(() => this.rendreCta(), 0);
      this.racine.querySelectorAll('[data-qty-step], [data-pdp-qty-input]').forEach((el) => {
        el.addEventListener('click', rattraper);
        el.addEventListener('change', rattraper);
      });

      if (this.form) {
        this.form.addEventListener(
          'submit',
          (evenement) => {
            // Deuxième passage après un échec : on s'efface, `product-form`
            // reprend l'ajout et affichera son message.
            if (this.laisserPasser) {
              this.laisserPasser = false;
              return;
            }
            if (this.mode === 'abo') {
              evenement.preventDefault();
              evenement.stopImmediatePropagation();
              this.bloc.dispatchEvent(
                new CustomEvent('ltb:tunnel', {
                  bubbles: true,
                  detail: { formule: this.formule, ...this.donnees },
                }),
              );
              return;
            }
            if (!this.rattrapagePossible()) return;
            evenement.preventDefault();
            evenement.stopImmediatePropagation();
            this.ajouterPuisProposer();
          },
          true,
        );
      }

      // Le tunnel signale sa fermeture : si le rattrapage vient d'être
      // décliné, le client attend son panier.
      document.addEventListener('ltb:tunnel-ferme', () => {
        if (!this.reponsePanier) return;
        const reponse = this.reponsePanier;
        this.reponsePanier = null;
        if (!this.panier) return;
        // `renderContents` ne retire `is-empty` que de `.drawer__inner` ; la
        // classe posée par Liquid sur `<cart-drawer>` lui-même reste, et le
        // tiroir s'ouvre sur « Complétez votre panier » alors que la box y
        // est bien. `product-form` la retire de son côté, pas nous.
        this.panier.classList.remove('is-empty');
        this.panier.renderContents(reponse);
      });

      // « Je m'abonne et j'en profite » : la fiche derrière suit le choix.
      document.addEventListener('ltb:offre-abo', () => {
        const input = this.bloc.querySelector('[data-offre-input][value="abo"]');
        if (!input || input.checked) return;
        input.checked = true;
        this.mode = 'abo';
        this.rendre();
      });
    }

    rattrapagePossible() {
      if (!this.donnees.rattrapage) return false;
      // Le tunnel n'est rendu ni hors gabarit Box ni pour un abonné actif :
      // son absence vaut refus, sans avoir à redire la condition ici.
      if (!document.querySelector('[data-ltb-tunnel]')) return false;
      if (this.donnees.rattrapageFrequence !== 'session') return true;
      return !dejaVu();
    }

    async ajouterPuisProposer() {
      const spinner = this.cta.querySelector('.loading__spinner');
      this.cta.setAttribute('aria-disabled', 'true');
      this.cta.classList.add('loading');
      spinner?.classList.remove('hidden');

      try {
        const corps = new FormData(this.form);
        const panier = document.querySelector('cart-notification')
          || document.querySelector('cart-drawer');
        if (panier) {
          corps.append('sections', panier.getSectionsToRender().map((s) => s.id));
          corps.append('sections_url', window.location.pathname);
        }
        const reponse = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: corps,
        });
        const resultat = await reponse.json();
        // Shopify répond 200 avec un `status` sur un refus métier (rupture…).
        if (resultat.status) throw new Error(resultat.description || resultat.message);

        this.panier = panier;
        this.reponsePanier = resultat;
        if (this.donnees.rattrapageFrequence === 'session') marquerVu();
        document.dispatchEvent(new CustomEvent('ltb:tunnel', {
          detail: { formule: this.formule, etape: 0 },
        }));
      } catch (erreur) {
        console.error('[ltb-rattrapage]', erreur);
        this.laisserPasser = true;
        this.form.requestSubmit();
      } finally {
        this.cta.classList.remove('loading');
        this.cta.removeAttribute('aria-disabled');
        spinner?.classList.add('hidden');
      }
    }

    rendre() {
      this.cartes.forEach((carte) => {
        carte.classList.toggle('is-selected', carte.dataset.offre === this.mode);
      });
      this.formules.forEach((carte) => {
        carte.classList.toggle('is-selected', carte.dataset.formule === this.formule);
      });

      const abo = this.mode === 'abo';
      if (this.config) this.config.hidden = !abo;
      // Un abonnement ne se multiplie pas.
      if (this.qty) this.qty.hidden = abo;

      if (this.desEl) {
        const cents = abo && this.formule === 'annuel'
          ? this.donnees.desAnnuel
          : this.donnees.desMensuel;
        if (typeof cents === 'number') this.desEl.textContent = euro(cents);
      }

      if (this.conditionsEl) {
        this.conditionsEl.textContent = this.formule === 'annuel'
          ? "Engagement de 12 mois. Au terme des 12 mois, l’abonnement continue au même tarif, sans engagement, et devient résiliable à tout moment."
          : 'Sans engagement, résiliable à tout moment depuis votre compte.';
      }

      this.rendreCta();

      // La barre collée du mobile affiche le prix du jour, qui n'est pas
      // celui du haut de fiche dès que l'abonnement est choisi.
      const des = this.formule === 'annuel' ? this.donnees.desAnnuel : this.donnees.desMensuel;
      document.dispatchEvent(new CustomEvent('ltb:offre', {
        detail: abo
          ? { prix: this.donnees.prixOffre, note: `puis dès ${euro(des)}/mois` }
          : null,
      }));
    }

    rendreCta() {
      if (!this.ctaText) return;
      // Sur une box épuisée, le bouton est desactive des le rendu serveur et
      // porte « Épuisé ». Choisir l'abonnement le rebaptisait « Continuer avec
      // cette offre » : un libelle d'action sur un bouton grise, sans que rien
      // n'explique pourquoi il ne repond pas.
      if (this.cta && this.cta.disabled) return;
      if (this.mode === 'abo') {
        this.ctaText.textContent = 'Continuer avec cette offre';
      } else if (this.ctaText.textContent.trim() === 'Continuer avec cette offre') {
        this.ctaText.textContent = this.labelUnique;
      }
    }
  }

  const demarrer = () => {
    document.querySelectorAll('.ltb-pdp__inner').forEach((racine) => {
      if (racine.dataset.ltbBoxPret) return;
      if (!racine.querySelector('[data-ltb-offre]')) return;
      racine.dataset.ltbBoxPret = '1';
      new OffreBox(racine);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', demarrer);
  } else {
    demarrer();
  }
})();
